import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Grid } from '@elfsight/lib-masonry-grid';
import { createReadableClass, useIsMobile } from '@elfsight/widget-utils';
import type { ButtonProps } from '../../../button';
import { LoadMoreButton } from '../LoadMoreButton';
import type { DataSourceProps, LayoutAPI, MappingFunction } from '../types';
import { transformDataSource, useLayoutData } from '../utils';

export type ListLayoutProps = {
  renderItem: MappingFunction;
  dataSource: DataSourceProps;
  itemSpacing: number;
  itemsPerPage?: number;
  itemsPerPageOnMobile?: number;
  showLoadMoreButton?: boolean;
  loaderColor?: string;
  loadMoreButtonText?: string;
  loadMoreButton?: ButtonProps;
  withPagination?: boolean;
  className?: string;
};

export const ListLayout = forwardRef<LayoutAPI, ListLayoutProps>(
  (
    {
      renderItem,
      withPagination = false,
      dataSource,
      itemsPerPage,
      itemsPerPageOnMobile,
      itemSpacing,
      showLoadMoreButton,
      loaderColor,
      loadMoreButtonText,
      loadMoreButton,
      className
    },
    forwardingRef
  ) => {
    const isMobile = useIsMobile();
    const dataSourceFormat = transformDataSource(dataSource);

    const {
      data: initialData,
      hasNext: hasNextFetch,
      next: handleNextFetch,
      status
    } = dataSourceFormat;

    const isLoading = status === 'pending';

    const itemsWithPagination = isMobile ? itemsPerPageOnMobile : itemsPerPage;

    const totalItemsPerPage =
      withPagination && itemsWithPagination
        ? itemsWithPagination
        : initialData.length;

    const {
      data,
      hasNext,
      loadNext,
      reset: resetLayout
    } = useLayoutData({
      data: initialData,
      itemsCountPerPage: totalItemsPerPage,
      hasNextFetch,
      handleNextFetch
    });

    useImperativeHandle(forwardingRef, () => ({ resetLayout }), [resetLayout]);

    useEffect(() => {
      resetLayout();
    }, [showLoadMoreButton]);

    return (
      <>
        <Grid
          className={createReadableClass(
            {
              component: 'list-layout'
            },
            [className]
          )}
          columns={1}
          gap={itemSpacing}
        >
          {data.map(renderItem)}
        </Grid>
        {withPagination && showLoadMoreButton && (
          <LoadMoreButton
            hasNext={hasNext}
            next={loadNext}
            isLoading={isLoading}
            text={loadMoreButtonText}
            gap={itemSpacing}
            loadMoreButton={loadMoreButton}
            loaderColor={loaderColor}
          />
        )}
      </>
    );
  }
);
